/**
 * oct20a S1 — voluntary diagnostic evidence and the session conclusion (pure: no client, no I/O).
 *
 * Three different things, never one another:
 *   - INTERIM EVIDENCE   what a technician tested, measured, found, reasoned or still wonders —
 *                        recorded any time during their diagnostic work (record_my_diagnostic_evidence).
 *                        Never an outcome.
 *   - SESSION CONCLUSION what ONE technician's session concluded when their Stop does not end the
 *                        diagnostic work (a colleague still works it, or the repair continues on its
 *                        next day). CORE-I vocabulary + confidence. Never the job's outcome.
 *   - JOB OUTCOME        the diagnosis's result, recorded once by whoever ends the diagnostic work
 *                        (unchanged: DiagnosticPauseCapture / the Stop's captures).
 *
 * Measurements typed here are the technician's reported values (SP-05: a human assertion), and the
 * wording says so.
 */
import type { DiagnosticConfidence, DiagnosticOutcome } from '@/types/diagnostic';

export const EVIDENCE_TEXT_MAX = 4000;
export const EVIDENCE_TESTS_MAX = 25;

export interface EvidenceTestDraft {
  testName: string;
  result: string;
  /** What the technician read, in their words (e.g. "28 psi (spec 55-62)"). */
  measurement: string;
}

export interface EvidenceDraft {
  findings?: string;
  reasoning?: string;
  unresolvedQuestions?: string;
  /** OCT28 #2: the repair the technician recommends — a recommendation for the front office, never an approved job. */
  recommendation?: string;
  confidence?: DiagnosticConfidence | null;
  tests?: EvidenceTestDraft[];
}

const clean = (s: string | undefined | null) => (s ?? '').trim();

/** The tests the technician actually filled in (a row with no test name is not a test). */
export function filledTests(tests: readonly EvidenceTestDraft[] | undefined): EvidenceTestDraft[] {
  return (tests ?? []).filter((t) => clean(t.testName) !== '');
}

/** Nothing to record: every text empty and no named test. */
export function evidenceIsEmpty(d: EvidenceDraft): boolean {
  return clean(d.findings) === '' && clean(d.reasoning) === '' && clean(d.unresolvedQuestions) === '' && clean(d.recommendation) === ''
    && filledTests(d.tests).length === 0;
}

/** Why the draft cannot be recorded yet, in the technician's words (null = it can). */
export function evidenceProblem(d: EvidenceDraft): string | null {
  if (evidenceIsEmpty(d)) return 'Write what you tested, found or still need to check.';
  for (const [label, v] of [['Findings', d.findings], ['Reasoning', d.reasoning], ['Open questions', d.unresolvedQuestions], ['Recommended repair', d.recommendation]] as const) {
    if (clean(v).length > EVIDENCE_TEXT_MAX) return `${label} is too long (${EVIDENCE_TEXT_MAX} characters at most).`;
  }
  if (filledTests(d.tests).length > EVIDENCE_TESTS_MAX) return `At most ${EVIDENCE_TESTS_MAX} tests per entry.`;
  return null;
}

/** The wire shape record_my_diagnostic_evidence validates. Only filled fields travel. */
export function evidencePayload(d: EvidenceDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (clean(d.findings)) out.findings = clean(d.findings);
  if (clean(d.reasoning)) out.reasoning = clean(d.reasoning);
  if (clean(d.unresolvedQuestions)) out.unresolved_questions = clean(d.unresolvedQuestions);
  if (clean(d.recommendation)) out.recommendation = clean(d.recommendation);
  if (d.confidence != null) out.confidence = d.confidence;
  const tests = filledTests(d.tests);
  if (tests.length > 0) {
    out.tests = tests.map((t) => ({
      test_name: clean(t.testName),
      ...(clean(t.result) ? { result: clean(t.result) } : {}),
      ...(clean(t.measurement) ? { values: { reported: clean(t.measurement) } } : {}),
    }));
  }
  return out;
}

export interface SessionConclusionDraft {
  disposition: DiagnosticOutcome | null;
  confidence: DiagnosticConfidence | null;
  findings?: string;
  unresolvedQuestions?: string;
}

export const EMPTY_SESSION_CONCLUSION: SessionConclusionDraft = { disposition: null, confidence: null };

/** A session conclusion is complete when the technician chose what the session found and how sure they are. */
export function sessionConclusionComplete(c: SessionConclusionDraft | undefined): boolean {
  return !!c && c.disposition !== null && c.confidence !== null;
}

/** The wire shape finish_my_work validates in p_session_conclusions. */
export function sessionConclusionPayload(jobId: string, c: SessionConclusionDraft): Record<string, unknown> {
  if (c.disposition === null || c.confidence === null) throw new Error('incomplete session conclusion');
  return {
    job_id: jobId,
    disposition: c.disposition,
    confidence: c.confidence,
    ...(clean(c.findings) ? { findings: clean(c.findings) } : {}),
    ...(clean(c.unresolvedQuestions) ? { unresolved_questions: clean(c.unresolvedQuestions) } : {}),
  };
}

/** What the Stop dialog says a session conclusion is — and is not. */
export const SESSION_CONCLUSION_EXPLAINER =
  'Your work on this diagnosis ends, but the diagnosis does not: say what YOUR session found. '
  + 'This is not the final diagnosis — whoever finishes it records that.';

export type EvidencePhase = 'RUNNING' | 'PAUSED' | 'AFTER_SESSION' | 'AT_STOP';

export const EVIDENCE_PHASE_LABELS: Record<EvidencePhase, string> = {
  RUNNING: 'while working',
  PAUSED: 'while paused',
  AFTER_SESSION: 'after their session',
  AT_STOP: 'when they stopped',
};

/** The server's refusal of record_my_diagnostic_evidence, in the technician's words. */
export function evidenceRefusalMessage(outcome: string, reason?: string): string {
  switch (outcome) {
    case 'NOT_TECHNICIAN': return 'Only a technician can record diagnostic findings.';
    case 'NOT_YOUR_DIAGNOSTIC_WORK': return 'You can record findings only on a diagnosis you are working or have worked.';
    case 'DIAGNOSIS_CONCLUDED': return 'This diagnosis already has its outcome; findings are closed.';
    case 'NOT_A_DIAGNOSTIC_JOB': return 'Inspection findings are recorded in the inspection, not here.';
    case 'JOB_NOT_ON_TICKET': return 'That job is no longer on this ticket.';
    case 'INVALID_EVIDENCE': return reason === 'empty_evidence'
      ? 'Write what you tested, found or still need to check.' : 'Some of this cannot be saved. Check the entries and try again.';
    case 'TICKET_NOT_AVAILABLE': return 'This ticket is not available.';
    default: return 'Your findings were not saved. Try again.';
  }
}
